import { isApolloError } from '@apollo/client';
import _ from 'lodash';
import { BlockEmbed } from 'quill/blots/block';
import type { AttributeMap } from 'quill/core';
import Quill, { Delta } from 'quill/core';
import React from 'react';
import { createPortal } from 'react-dom';
import Logger from '../../utils/Logger';
import parseGraphQLError from '../../utils/error/parseGraphQLError';
import { isFeatureEnabled } from '../../utils/features';
import { getImageUploadsForFiles } from '../utils/getImagePlaceholderInfo';
import uploadPostImage, {
  overlargeImageMessage,
} from '../../utils/network/uploadPostImage';
import type { CarouselItem } from '../image/Carousel';
import { areCarouselImages } from '../image/Carousel';
import type { ImageCarouselHeight } from '../image/ImageCarousel';
import ImageCarousel, { isImageCarouselHeight } from '../image/ImageCarousel';
import ImageContainer from '../image/ImageContainer';
import type { DropSide } from '../image/ImageNode';
import type { ScrollBlot } from '../types';
import { findBlotByNode } from '../utils/findByNode';
import safeParseUrl from '../utils/safeParseUrl';
import showEditorToast from '../utils/showEditorToast';
import Comment from './Comment';
import Diff from './Diff';
import Height from './Height';
import type { Store } from './ReactBlockEmbed';
import { createStore } from './ReactBlockEmbed';
import Reaction from './Reaction';
import Scroll from './Scroll';
import Span from './Span';
import generateDeltaRandomId from '../utils/generateDeltaRandomId';
import VersionChange from './VersionChange';
import Queue from '../utils/Queue';

const Parchment = Quill.import('parchment');

const normalizeAttributeValue = (name: string, value: unknown) => {
  if (name === 'width' || name === 'height') {
    // Percentage values are invalid as width/height property for <img/> in HTML.
    return Number(value) || undefined;
  }

  return String(value);
};

export const compactCarouselItem = (item: CarouselItem): CarouselItem => {
  const { source, ...rest } = item;
  return { source, ..._.omitBy(rest, _.isNil) };
};

Delta.registerEmbed('image', {
  compose(_a: CarouselItem[], b: CarouselItem[]) {
    return b;
  },
  transform(a: CarouselItem[], b: CarouselItem[], priority: boolean) {
    return priority ? b : a;
  },
  invert(_change: CarouselItem[], base: CarouselItem[]) {
    return base;
  },
});

type UploadPayload = File | string; // The file or URL that will be uploaded.
export type ImageValue =
  | CarouselItem[] // New format
  | string // Legacy format
  | true; // Placeholder indicating the image is being uploaded by a collaborator

interface State {
  value: CarouselItem[] | true;
  height?: ImageCarouselHeight | null;
  carouselIndex?: number;
}

function isUploading(value: State['value']) {
  return (
    (Array.isArray(value) &&
      value.some((item) => item.uploadPayload != null)) ||
    value === true
  );
}

function attributesToString(attributes: NamedNodeMap) {
  return [...attributes]
    .map((attr) => `${attr.nodeName}="${attr.nodeValue ?? ''}"`)
    .join(' ');
}

function carouselItemToHtml(
  { source, caption }: CarouselItem,
  attributes = '',
) {
  const captionHTML = caption
    ? `<figcaption>${_.escape(caption)}</figcaption>`
    : '';

  return `<figure${
    attributes ? ` ${attributes}` : ''
  }><img src="${source}" />${captionHTML}</figure>`;
}

function getFilteredValue(value: State['value']) {
  if (value === true) return value;

  const hasUploadPayloads = value.some((item) => item.uploadPayload != null);

  if (hasUploadPayloads) {
    const imagesWithoutUploadPayloads = value.filter(
      (item) => item.uploadPayload == null,
    );

    if (imagesWithoutUploadPayloads.length === 0) {
      return true;
    }

    return imagesWithoutUploadPayloads;
  }

  return value;
}

class ImageBlot extends BlockEmbed {
  static blotName = 'image';
  static className = 'ql-image';
  static tagName = 'FIGURE';

  static ALLOWED_ATTRIBUTES = ['float'];
  static ALLOWED_PROPERTIES = ['width', 'caption'];

  static isImageOrImageContainerNode(node: unknown): node is HTMLElement {
    return (
      node instanceof Element &&
      (node.tagName === 'IMG' || node.closest('.image-container') != null)
    );
  }

  static isWithinCarousel(node: unknown): node is Element {
    return node instanceof Element && node.closest('.image-carousel') != null;
  }

  static addImageIdsIfNeeded(images: CarouselItem[]) {
    return areCarouselImages(images)
      ? images.map((item) => {
          if (item.id == null) {
            return {
              ...item,
              id: generateDeltaRandomId(),
            };
          }
          return item;
        })
      : images;
  }

  static getStateFromValue(value: ImageValue): State {
    if (typeof value === 'string') {
      return { value: [{ source: value }] };
    }

    if (Array.isArray(value)) {
      return {
        value: value.map((v) =>
          compactCarouselItem({
            id: v.id,
            uploadPayload: v.uploadPayload,
            source: v.source,
            width: this.ALLOWED_PROPERTIES.includes('width')
              ? v.width
              : undefined,
            caption: this.ALLOWED_PROPERTIES.includes('caption')
              ? v.caption
              : undefined,
          }),
        ),
      };
    }

    return { value: true };
  }

  static imageUploadQueue: Queue<string | null> = new Queue();

  static async upload(uploadPayload: UploadPayload, domNode: HTMLElement) {
    let uploadedURL: string | null = null;

    try {
      uploadedURL = await this.imageUploadQueue.enqueue(() =>
        uploadPostImage(uploadPayload),
      );
    } catch (err) {
      const blot = findImageBlotByNode(domNode);
      if (blot) {
        const isUploadPayloadUrl =
          typeof uploadPayload === 'string' &&
          safeParseUrl(uploadPayload)?.protocol === 'https:';

        const canKeepImage = isUploadPayloadUrl;

        const isUnprocessable =
          err instanceof Error &&
          isApolloError(err) &&
          err.graphQLErrors
            ?.map(parseGraphQLError)
            .some(({ code }) => code === 'UNPROCESSABLE_ENTITY');

        if (isUnprocessable) {
          showEditorToast({
            message:
              'We could not make a copy of the pasted image and it may be inaccessible to others in the future.',
            level: 'error',
          });
        } else if (!canKeepImage) {
          if (err.networkError && err.networkError.statusCode === 413) {
            showEditorToast({ message: overlargeImageMessage, level: 'error' });
          } else {
            showEditorToast({
              message: 'Image could not be uploaded.',
              level: 'error',
            });
          }
        }

        if (canKeepImage) {
          blot.cleanUpload(uploadPayload, uploadPayload);
        } else if (blot.isCarousel()) {
          blot.removeImageWithUploadPayloadFromCarousel(uploadPayload);
        } else {
          blot.remove();
        }
      }
    }

    if (uploadedURL) {
      findImageBlotByNode(domNode)?.cleanUpload(uploadPayload, uploadedURL);
    }
  }

  static create(value: ImageValue) {
    if (typeof value === 'string') {
      Logger.warn('Received legacy image value');
    }

    const state = this.getStateFromValue(value);

    const figure = super.create(value) as HTMLElement;
    figure.setAttribute('contenteditable', 'false');

    if (Array.isArray(state.value)) {
      state.value.forEach(({ uploadPayload }) => {
        if (uploadPayload) {
          this.upload(uploadPayload, figure);
        }
      });
    } else if (state.value === true) {
      // This happens when other collaborator is uploading the image.
      figure.setAttribute('data-uploading-placeholder', '');
    }

    return figure;
  }

  static formats(domNode: HTMLElement) {
    const styleFormats: AttributeMap = {};
    if (domNode.tagName === 'IMG') {
      if (typeof domNode.style.float === 'string') {
        const float = domNode.style.float.toLowerCase();
        if (float === 'left' || float === 'right') {
          styleFormats.float = float;
        }
      }
    }
    const attributeFormats = this.ALLOWED_ATTRIBUTES.reduce((formats, name) => {
      const attribute = `data-${name}`;
      if (!domNode.hasAttribute(attribute)) {
        return formats;
      }
      const value = domNode.getAttribute(attribute);
      return value
        ? {
            ...formats,
            [name]: normalizeAttributeValue(name, value),
          }
        : formats;
    }, {});

    return { ...styleFormats, ...attributeFormats };
  }

  static match(url: string) {
    return /\.(jpe?g|gif|png|svg)$/.test(url);
  }

  static figureToCarouselItem(figure: Element) {
    const imgNode = figure.querySelector('img');
    const figcaptionNode = figure.querySelector('figcaption');
    const src = imgNode?.getAttribute('src');
    if (!src) return null;

    const image: CarouselItem = { source: src };
    if (this.ALLOWED_PROPERTIES.includes('caption')) {
      const caption = figcaptionNode?.innerText ?? figcaptionNode?.textContent;
      if (caption) {
        image.caption = caption;
      }
    }
    return image;
  }

  static value(domNode: HTMLElement) {
    if (domNode.tagName === 'IMG') {
      const source = domNode.getAttribute('src');
      return source ? [{ source }] : null;
    }

    if (domNode.tagName === ImageBlot.tagName) {
      const nestedFigures = domNode.querySelectorAll(':scope figure');
      if (nestedFigures.length > 0) {
        return _.compact(
          Array.from(nestedFigures).map(this.figureToCarouselItem.bind(this)),
        );
      }

      return [this.figureToCarouselItem(domNode)];
    }

    return null;
  }

  id = _.uniqueId();
  store: Store<State>;

  constructor(scroll: ScrollBlot, domNode: Node, value: ImageValue) {
    // @ts-expect-error
    super(scroll, domNode, value);
    this.store = createStore(this.statics.getStateFromValue(value));
  }

  value(): { image: CarouselItem[] | true } {
    const { value } = this.store.getState();
    return { image: getFilteredValue(value) };
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  formats() {
    return {
      ...this.attributes?.values(),
      ...this.statics.formats(this.domNode, this.scroll),
    };
  }

  format(name: string, value: unknown) {
    if (
      name === Comment.attrName ||
      name === Reaction.attrName ||
      name === Span.attrName ||
      name === Height.attrName ||
      name === Diff.attrName ||
      name === VersionChange.attrName
    ) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor) {
        // @ts-expect-error
        this.attributes.attribute(attributor, value);
      }

      if (
        name === Height.attrName &&
        (value === null || isImageCarouselHeight(value))
      ) {
        this.store.setState({ height: value });
      }
    } else if (this.statics.ALLOWED_ATTRIBUTES.includes(name)) {
      const attribute = `data-${name}`;
      if (value) {
        this.domNode.setAttribute(attribute, String(value));
      } else {
        this.domNode.removeAttribute(attribute);
      }
    } else {
      super.format(name, value);
    }
  }

  getReferenceNodeForBounds() {
    return (
      this.domNode.querySelector('.image-container') ||
      this.domNode.querySelector('img') ||
      this.domNode
    );
  }

  html() {
    const { value } = this.store.getState();
    if (!Array.isArray(value)) return '';

    const firstImage = value[0];
    if (!firstImage) return '';

    const attributes = attributesToString(this.domNode.attributes);

    if (value.length === 1) {
      return carouselItemToHtml(firstImage, attributes);
    }

    return `<figure ${attributes}>${value
      .map((item) => carouselItemToHtml(item))
      .join('')}</figure>`;
  }

  isUploading() {
    return isUploading(this.store.getState().value);
  }

  updateContent(value: CarouselItem[]) {
    this.store.setState(this.statics.getStateFromValue(value));
  }

  private removeImageWithUploadPayloadFromCarousel(
    uploadPayload: UploadPayload,
  ) {
    const images = this.store.getState().value;

    if (Array.isArray(images)) {
      const newValue = images.filter((image) => {
        return (
          image.uploadPayload !== uploadPayload &&
          image.source !== uploadPayload
        );
      });

      this.handleUserChange({ value: newValue });
    }
  }

  private async cleanUpload(uploadPayload: UploadPayload, src: string) {
    try {
      // Pre cache image so when we update the src, users won't see a flash
      await preCacheImage(src);
    } finally {
      const images = this.store.getState().value;
      if (Array.isArray(images)) {
        const newValue = images.map((image) => {
          if (
            image.uploadPayload === uploadPayload ||
            image.source === uploadPayload
          ) {
            return compactCarouselItem({
              ...image,
              source: src,
              uploadPayload: undefined,
            });
          }
          return image;
        });

        this.handleUserChange({ value: newValue });
      } else {
        const newValue = [
          compactCarouselItem({
            ...getFirstImage(this.store.getState()),
            source: src,
            uploadPayload: undefined,
          }),
        ];
        this.handleUserChange({ value: newValue });
      }
    }
  }

  private handleUserChange(change: State) {
    const value = Array.isArray(change.value)
      ? this.statics.addImageIdsIfNeeded(change.value)
      : change.value;
    const correctedChange = { ...change, value };
    const currentValue = this.value().image;
    const filteredValue = getFilteredValue(correctedChange.value);

    this.store.setState(correctedChange);

    if (!_.isEqual(currentValue, filteredValue)) {
      // @ts-expect-error
      this.scroll.emitEmbedUpdate(this, filteredValue);
    }
  }

  public async uploadFiles(files: File[], index?: number) {
    const state = this.store.getState();
    const images = Array.isArray(state.value) ? state.value : null;
    if (!images) return;

    const placeholders = await getImageUploadsForFiles(files);

    const nextImages =
      index != null
        ? images.flatMap((item, i) => {
            if (i === images.length - 1 && index === images.length) {
              return [item, ...placeholders];
            }
            if (i === index) {
              return [...placeholders, item];
            }
            return item;
          })
        : [...images, ...placeholders];

    placeholders.forEach((placeholder) => {
      if (placeholder.uploadPayload) {
        ImageBlot.upload(placeholder.uploadPayload, this.domNode);
      }
    });

    const attributes = this.attributes?.values() ?? {};

    if (attributes.span === 'half') {
      this.format('span', null);
    }

    this.format('float', null);

    const nextImagesWithoutWidth = nextImages.map((item) =>
      _.omit(item, ['width']),
    );

    this.handleUserChange({ value: nextImagesWithoutWidth });
  }

  public isCarousel() {
    const state = this.store.getState();
    const images = Array.isArray(state.value) ? state.value : null;
    const formats = this.formats();

    if (!images || formats.float != null) return false;
    return areCarouselImages(images);
  }

  renderPortal({
    selected,
    readOnly,
  }: {
    selected: boolean;
    readOnly: boolean;
  }) {
    const { Connect } = this.store;
    const key = `editor_${this.statics.blotName}_${this.id}`;
    const captionsAllowed = this.statics.ALLOWED_PROPERTIES.includes('caption');

    return createPortal(
      <Connect>
        {(state) => {
          const images = Array.isArray(state.value) ? state.value : null;
          if (!images) return null;

          if (this.isCarousel() && areCarouselImages(images)) {
            return (
              <ImageCarousel
                selected={selected}
                images={images}
                onReorder={(next) =>
                  this.handleUserChange({ value: [...next] })
                }
                onIndexChange={(index) =>
                  this.store.setState({ carouselIndex: index })
                }
                onRemove={(image) => {
                  this.handleUserChange({
                    value: images.filter((item) => item !== image),
                  });
                }}
                onCaptionChange={(image, caption) => {
                  this.handleUserChange({
                    value: images.map((item) => {
                      if (item === image) {
                        return compactCarouselItem({
                          ...item,
                          caption: caption === '' ? undefined : caption,
                        });
                      }
                      return item;
                    }),
                  });
                }}
                height={state.height ?? 'default'}
                captionsAllowed={captionsAllowed}
                onAddImages={(files, index) => this.uploadFiles(files, index)}
              />
            );
          }

          const firstImage = images[0];
          if (!firstImage) return null;

          const onDrop = (files: File[], side: DropSide) =>
            this.uploadFiles(files, side === 'left' ? 0 : 1);

          return (
            <ImageContainer
              onDrop={
                isFeatureEnabled('image_carousel') && !readOnly
                  ? onDrop
                  : undefined
              }
              src={firstImage.source}
              uploading={this.isUploading()}
              caption={firstImage.caption}
              onCaptionChange={
                captionsAllowed
                  ? (caption) => {
                      this.handleUserChange({
                        value: [
                          compactCarouselItem({
                            ...firstImage,
                            caption: caption === '' ? undefined : caption,
                          }),
                        ],
                      });
                    }
                  : undefined
              }
              width={firstImage.width}
            />
          );
        }}
      </Connect>,
      this.domNode,

      // Hint to the reconciler otherwise the whole component will be torn down and re-created.
      // This is useful to prevent the CSS animation of progress bar from restarting.
      key,
    );
  }
}

function findImageBlotByNode(node: Node) {
  const blot = findBlotByNode(node);
  return blot instanceof ImageBlot ? blot : null;
}

function getFirstImage({ value }: State) {
  return Array.isArray(value) ? value[0] : null;
}

function preCacheImage(url: string) {
  return Promise.race<void>([
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = url;
    }),
    new Promise((_resolve, reject) => {
      setTimeout(() => reject(new Error('Image load exceeded timeout')), 2000);
    }),
  ]);
}

export default ImageBlot;
